import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom, inject, ErrorHandler } from '@angular/core';
import { environment } from '../environments/environment';
import { AADPostLoginProcessor, ApiUrlInterceptorService, AppSettings, AsSwalService, AsamcoAuthGuard, AsamcoAuthService, AsamcoBaseModule, AuthInterceptorService, AuthenticatedUserBaseApiAddressProvider, BASE_API_ADDRESS_PROVIDER, CustomTranslationLoader, ENVIRONMENT, IeHeaderInterceptor, LATE_LOGIN_PROCESSOR, MENU_SERVICE, MenuService, NullSignalRNotificationHandler, OidcConfigSetupService, POST_LOGIN_PROCESSOR, SIGNALR_NOTIFICATION_HANDLER, defaultL10nLoader, getSupportedLanguagesConfig, loadOidcConfig } from '@asamco/base';
import { APP_BASE_HREF, DatePipe } from '@angular/common';
import { L10nLoader, L10nTranslationModule } from 'angular-l10n';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { FixMissingUserapprovalInterceptor, SelectedCompanyInterceptor } from '@asamco/application-link/feature';
import { Router, Routes, provideRouter, withHashLocation } from '@angular/router';
import { AuthModule, StsConfigLoader } from 'angular-auth-oidc-client';
import { BrowserModule } from '@angular/platform-browser';
import { createErrorHandler, TraceService } from '@sentry/angular';
import { CSPLoginProcessor, TicketIntegrationLoginProcessor } from '@asamco/remote';

/**
 * App routes
 */
/* istanbul ignore next */
const routes: Routes = [
	{
		path: 'auth',
		loadChildren: () => import('@asamco/b2c-login').then(m => m.B2cLoginModule),
	},
	{
		path: '',
		canMatch: [(r,s) => inject(AsamcoAuthGuard).canMatch(r, s)],
		loadChildren: () => import('./app-shell/app-shell-routes').then(m => m.routes),
	},
	{ path: '**', redirectTo: '', pathMatch: 'full' }
];

export const appConfig: ApplicationConfig = {
	providers: [
		provideRouter(routes, withHashLocation()),
		importProvidersFrom(AuthModule.forRoot({
			loader: {
				provide: StsConfigLoader,
				useFactory: loadOidcConfig,
				deps: [AppSettings, OidcConfigSetupService],
			}
		})),
		importProvidersFrom(L10nTranslationModule.forRoot(getSupportedLanguagesConfig(['enUS'], 'enUS'), {
			translationLoader: CustomTranslationLoader
		})),
		importProvidersFrom(HttpClientModule),
		importProvidersFrom(BrowserModule),
		importProvidersFrom(AsamcoBaseModule),
		{ provide: ENVIRONMENT, useValue: environment },
		// In Angular 1.6 hash based routing now has #! for google ajax crawlers, we should match in Angular
		// https://docs.angularjs.org/guide/migration#commit-aa077e8
		{ provide: APP_BASE_HREF, useValue: '!' },
		{
			provide: APP_INITIALIZER,
			useFactory: defaultL10nLoader,
			deps: [L10nLoader],
			multi: true,
		},
		{
			provide: APP_INITIALIZER,
			useFactory: (menuService: MenuService) => () => menuService.init(),
			deps: [MenuService],
			multi: true,
		},
		{
			provide: MENU_SERVICE,
			useClass: MenuService,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: ApiUrlInterceptorService,
			multi: true,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: AuthInterceptorService,
			multi: true,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: IeHeaderInterceptor,
			multi: true,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: SelectedCompanyInterceptor,
			multi: true,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: FixMissingUserapprovalInterceptor,
			multi: true,
		},
		// Add the legacy auth interceptor so all requests will get a guid on them.
		{
			provide: BASE_API_ADDRESS_PROVIDER,
			useClass: AuthenticatedUserBaseApiAddressProvider,
			deps: [AsamcoAuthService],
		},
		{
			provide: POST_LOGIN_PROCESSOR,
			useClass: AADPostLoginProcessor,
			deps: [HttpClient],
			multi: true,
		},
		{
			provide: LATE_LOGIN_PROCESSOR,
			useClass: CSPLoginProcessor,
			deps: [HttpClient],
			multi: true,
		},
		{
			provide: SIGNALR_NOTIFICATION_HANDLER,
			useClass: NullSignalRNotificationHandler,
			multi: true,
		},
		{
			provide: ErrorHandler,
			useValue: createErrorHandler({
				showDialog: false,
			}),
		},
		{
			provide: TraceService,
			deps: [Router],
		},
		AsSwalService,
		DatePipe
	]
};
